import React, { useEffect, useState } from "react";
import { Card } from "@shared/ui/card";
import TariffCard from "./card";
import {
  useRecentlyUpdatedRates,
  useRecentlyUpdatedPlans,
  usePlanDashboard,
} from "../../hooks";
import { usePageViewTimer } from "@shared/hooks/usePageViewTimer";
import { useDateNormalization } from "@shared/hooks/useDateNormalization";

const OverviewTab = () => {

  usePageViewTimer({pageName: "Plan & Tariff Dashboard"})
  const [tariffLimit, setTariffLimit] = useState(3);
  const [planLimit, setPlanLimit] = useState(3);

  const [tariffItems, setTariffItems] = useState<any[]>([]);
  const [planItems, setPlanItems] = useState<any[]>([]);

  // ✅ KPI Dashboard data
  const {
    data: dashboardData,
    isLoading: isDashboardLoading,
    isError: isDashboardError,
  } = usePlanDashboard();
  const { formatToUserDate } = useDateNormalization();

  // ✅ Fetch recently updated data with increasing limit
  const { data: tariffData, isError: isTariffError } =
    useRecentlyUpdatedRates(tariffLimit);
  const { data: planData, isError: isPlanError } =
    useRecentlyUpdatedPlans(planLimit);

  // ✅ Handle tariff updates
  useEffect(() => {
    if (tariffData?.result) {
      const newTariffs = tariffData.result.map((item) => ({
        name: item.rateName,
        type: item.rateType,
        date: `${formatToUserDate(item.modifyDate)}, ${item.modifyTime}`
      }));
      setTariffItems(newTariffs); // replace data on each limit change
    }
  }, [tariffData]);

  // ✅ Handle plan updates
  useEffect(() => {
    if (planData?.result) {
      const newPlans = planData.result.map((item) => ({
        name: item.planName,
        type: item.category || "N/A",
        date: `${formatToUserDate(item.modifyDate)}, ${item.modifyTime}`,
      }));
      setPlanItems(newPlans); // replace data on each limit change
    }
  }, [planData]);

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="p-4">
          <p className="text-sm text-gray-500">Total Plans</p>
          <h2 className="text-2xl font-semibold">
            {dashboardData?.totalPlans ?? "--"}
          </h2>
          <p className="text-xs text-gray-400">
            {dashboardData?.activePlans ?? 0} active,{" "}
            {dashboardData?.inactivePlans ?? 0} inactive
          </p>
        </Card>

        <Card className="p-4">
          <p className="text-sm text-gray-500">Total Tariffs</p>
          <h2 className="text-2xl font-semibold">
            {dashboardData?.totalRates ?? "--"}
          </h2>
          <p className="text-xs text-gray-400">
            {dashboardData?.activeRates ?? 0} active,{" "}
            {dashboardData?.inactiveRates ?? 0} inactive
          </p>
        </Card>

        <Card className="p-4">
          <p className="text-sm text-gray-500">Active Subscribers</p>
          <h2 className="text-2xl font-semibold">
            {dashboardData?.totalActiveConsumers ?? "--"}
          </h2>
          <p className="text-xs text-gray-400">
            {dashboardData?.consumerGrowthPercentage ?? 0}% from last month
          </p>
        </Card>
      </div>

      {/* Recently Updated Plans & Tariffs */}
      <div className="grid grid-cols-2 gap-6">
        {planItems.length > 0 ? (
          <TariffCard
            title="Recently Updated Plans"
            subtitle="Plans that were recently created or modified"
            items={planItems}
            onViewAllClick={() => setPlanLimit((prev) => prev + 3)}
          />
        ) : (
          <div className="flex items-center justify-center border rounded-lg p-6 h-full">
            <p className="text-gray-500">No Recently Updated Plans</p>
          </div>
        )}

        {tariffItems.length > 0 ? (
          <TariffCard
            title="Recently Updated Tariffs"
            subtitle="Tariffs that were recently created or modified"
            items={tariffItems}
            onViewAllClick={() => setTariffLimit((prev) => prev + 3)}
          />
        ) : (
          <div className="flex items-center justify-center border rounded-lg p-6 h-full">
            <p className="text-gray-500">No Recently Updated Tariffs</p>
          </div>
        )}
      </div>

      {(isTariffError || isPlanError || isDashboardError) && (
        <p className="text-sm text-red-500">Failed to load some data.</p>
      )}
    </div>
  );
};

export default OverviewTab;
